import { useEffect, useMemo, useRef, useState } from 'react';
import type { components } from '../../../types/schema';

type BettieMessage = components['schemas']['ChatRoomMessage'];

interface UseSequentialBettieRepliesParams {
  baseMessages: BettieMessage[];
  scrollToBottom?: (behavior?: ScrollBehavior) => void;
  initialGapMs?: number; // 강제 초기 간격(ms) - 1번째 → 2번째
  defaultDelayMs?: number; // 기본 지연(ms)
  showTypingDuringStagger?: boolean; // 지연 사이 타이핑 버블 노출 여부
}

interface UseSequentialBettieRepliesReturn {
  renderMessages: BettieMessage[];
  startSequentialReveal: (ids: string[], delays?: Record<string, number>) => void;
  resetSequentialReveal: () => void;
  isStaggering: boolean;
  visibleCount: number;
}

export const useSequentialBettieReplies = ({
  baseMessages,
  scrollToBottom,
  initialGapMs = 3000,
  defaultDelayMs = 1500,
  showTypingDuringStagger = true
}: UseSequentialBettieRepliesParams): UseSequentialBettieRepliesReturn => {
  const [staggerIds, setStaggerIds] = useState<string[]>([]);
  const [staggerVisibleCount, setStaggerVisibleCount] = useState(0);
  const staggerTimerRef = useRef<number | null>(null);
  const staggerDelaysRef = useRef<Record<string, number>>({});

  // 순차 노출 타이머
  useEffect(() => {
    if (staggerTimerRef.current) {
      clearTimeout(staggerTimerRef.current);
      staggerTimerRef.current = null;
    }

    if (staggerIds.length === 0) {
      setStaggerVisibleCount(0);
      return;
    }

    // 첫 응답은 즉시 노출
    setStaggerVisibleCount(1);

    let cancelled = false;
    const scheduleNext = (index: number) => {
      if (cancelled) return;
      if (index >= staggerIds.length) return;

      const prevIndex = index - 1;

      // 두 번째 메시지(인덱스 1)만 강제 초기 간격 적용
      const delay =
        index === 1
          ? initialGapMs
          : (staggerDelaysRef.current[staggerIds[prevIndex]] ?? defaultDelayMs);

      staggerTimerRef.current = window.setTimeout(() => {
        if (cancelled) return;
        setStaggerVisibleCount(index + 1);
        scheduleNext(index + 1);
      }, delay);
    };

    // 두 번째 메시지부터 예약 시작
    scheduleNext(1);

    return () => {
      cancelled = true;
      if (staggerTimerRef.current) {
        clearTimeout(staggerTimerRef.current);
        staggerTimerRef.current = null;
      }
    };
  }, [staggerIds, initialGapMs, defaultDelayMs]);

  // 순차 노출 진행 중 스크롤 유지
  useEffect(() => {
    if (staggerIds.length > 0 && staggerVisibleCount > 0) {
      scrollToBottom?.('smooth');
    }
  }, [staggerIds.length, staggerVisibleCount, scrollToBottom]);

  // 지정된 Bettie 응답 ID들은 visibleCount까지만 노출
  const renderMessages = useMemo(() => {
    if (staggerIds.length === 0) return baseMessages;
    const orderMap = new Map<string, number>(staggerIds.map((id, idx) => [id, idx]));
    const filtered = baseMessages.filter((m) => {
      const idx = orderMap.get(m.chatRoomMessageId);
      if (idx === undefined) return true;
      return idx < staggerVisibleCount;
    });

    // 지연 사이에는 맨 하단에 타이핑 버블 추가
    const hasMoreToReveal = staggerVisibleCount > 0 && staggerVisibleCount < staggerIds.length;
    const isTypingAlreadyPresent = baseMessages.some(
      (m) => m.messageType === 'notice' && m.content === '__typing__'
    );
    if (showTypingDuringStagger && hasMoreToReveal && !isTypingAlreadyPresent) {
      const lastChatRoomId = baseMessages[baseMessages.length - 1]?.chatRoomId ?? '';
      const lastBettieMsg = [...baseMessages].reverse().find((m) => !m.isMine);
      const typingMessage: BettieMessage = {
        chatRoomMessageId: `typing-seq-${lastChatRoomId || 'unknown'}`,
        chatRoomId: lastChatRoomId,
        userId: null,
        userProfileId: null,
        messageType: 'notice' as BettieMessage['messageType'],
        content: '__typing__',
        displayName: lastBettieMsg?.displayName ?? '',
        userProfilePictureUrl: lastBettieMsg?.userProfilePictureUrl ?? null,
        sentAt: new Date().toISOString(),
        seenAt: null
      } as BettieMessage;
      return [...filtered, typingMessage];
    }

    return filtered;
  }, [baseMessages, staggerIds, staggerVisibleCount, showTypingDuringStagger]);

  const startSequentialReveal = (ids: string[], delays?: Record<string, number>) => {
    staggerDelaysRef.current = delays ?? {};
    setStaggerIds(ids);
  };

  const resetSequentialReveal = () => {
    staggerDelaysRef.current = {};
    setStaggerIds([]);
    setStaggerVisibleCount(0);
    if (staggerTimerRef.current) {
      clearTimeout(staggerTimerRef.current);
      staggerTimerRef.current = null;
    }
  };

  return {
    renderMessages,
    startSequentialReveal,
    resetSequentialReveal,
    isStaggering: staggerIds.length > 0 && staggerVisibleCount > 0,
    visibleCount: staggerVisibleCount
  };
};
